import { z } from 'zod';
import { useForm, type SubmitHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useNavigate } from 'react-router';
import { toast } from 'react-toastify';
import { handleError } from '../apis';
import AuthApi from '../apis/AuthApi';
import { setToken } from '../apis/LocalStorageApi';
import RHFInput from './RHF/RHFInput';
import { RHFSubmitButton } from './RHF/RHFSubmitButton';
import { useUserDetails } from '../helpers/UserContext';
import { useEffect } from 'react';

const loginSchema = z.object({
  email: z.email('Required!').trim(),
  password: z
    .string('Required!')
    .trim()
    .min(6, { message: 'Password must be at least 6 characters long' })
    .refine(
      (value) => /[a-z]/.test(value),
      'Password must contain a lowercase character',
    )
    .refine(
      (value) => /[A-Z]/.test(value),
      'Password must contain a uppercase character',
    )
    .refine(
      (value) => /[!@#$%^&*]/.test(value),
      'Password must contain a special character (!@#$%^&*)',
    )
    .refine((value) => /\d/.test(value), 'Password must contain a number'),
});

type LoginSchema = z.infer<typeof loginSchema>;

export default function LoginForm() {
  const navigate = useNavigate();
  const [, , setUserFromToken, hasAuth] = useUserDetails();
  const isAuthorized = hasAuth();

  useEffect(() => {
    if (isAuthorized) {
      navigate('/alpacas');
    }
  }, [isAuthorized, navigate]);

  const {
    handleSubmit,
    register,
    formState: { errors, isSubmitting },
  } = useForm<LoginSchema>({
    resolver: zodResolver(loginSchema),
  });

  const handleLogin: SubmitHandler<LoginSchema> = async (values) => {
    try {
      const response = await AuthApi.login({ ...values, rememberMe: false });
      if (response.token) {
        setToken(response.token as string);
        setUserFromToken(response.token as string);
        toast.success('Login successful!');
        // navigate('/alpacas');
      } else {
        const errorResponse: string[] = [];
        for (const prop in response) {
          const value = (response as unknown as { [key: string]: string[] })[
            prop
          ];
          if (Array.isArray(value)) {
            errorResponse.push(value[0]);
          } else {
            errorResponse.push(value);
          }
        }
        const message = errorResponse.join(', ');
        toast.error(message);
      }
    } catch (error) {
      const message = await handleError(error);
      toast.error(message);
    }
  };
  return (
    <div
      className='flex w-[320px] flex-initial flex-col items-center justify-start self-auto rounded-2xl border border-gray-500 p-3 dark:border-gray-500 '
      id='login-form'
    >
      <p className='mb-2'>
        Log In or{' '}
        <Link className='font-bold' to='/register'>
          Sign Up
        </Link>
      </p>
      <form onSubmit={handleSubmit(handleLogin)} className='w-full'>
        <RHFInput
          name='email'
          label='Email'
          type='text'
          register={register('email')}
          errors={errors}
        />
        <RHFInput
          name='password'
          label='Password'
          type='password'
          register={register('password')}
          errors={errors}
        />
        <RHFSubmitButton isSubmitting={isSubmitting} />
      </form>
      <Link to='/forgotpassword' className='text-xs'>
        Forgot Password?
      </Link>
    </div>
  );
}
